import _memoize from 'lodash/memoize';
import { FULL_HD, HD } from '@bedrock-rdk/constants';
import SkeletonRectangle from '../../../../skeleton/skeleton.rectangle.component';
import { cacheResolver, makeDefaultStylingContext } from '../../../../utils/style';
import { identity, SCREEN_WIDTH } from '../../../../utils/sizes';
import { getBlockStyle, getTitleStyle } from '../../../../block/block.style';
import { HEIGHT as BUTTON_HEIGHT } from '../../../../button/button.style';
import { BORDER_RADIUS as BUTTON_BORDER_RADIUS } from '../../../../button/button.constants';
import {
  SIZE as ARROW_SIZE,
  BORDER_RADIUS as ARROW_BORDER_RADIUS,
} from '../../../../navigation-arrow/navigationArrow.style';
import { BORDER_RADIUS as CHANNEL_BORDER_RADIUS } from '../../../../square/square.style';
import { getLabelFont as getTabsFont } from '../../../selectors/selector/blockHeader/blockHeader.style';
import { GAP as EPG_BOX_GAP, BORDER_RADIUS as EPG_BOX_BORDER_RADIUS } from '../epgBox/epgBox.style';
import { HEIGHT as TIMELINE_HEIGHT, getTextStyle as getTimelineTextStyle } from '../epgTimeline/epgTimeline.style';
import { HEIGHT as NAV_HEIGHT, GAP_LEFT, GAP_RIGHT, TO_LIVE_MARGIN_RIGHT } from '../epgNav/epgNav.style';
import { PIXELS_PER_MINUTE } from '../epg.constants';
import {
  CHANNEL_SIZE,
  GAP,
  MAX_VISIBLE_ROWS,
  RIGHT_PADDING,
  TIMELINE_MARGIN_BOTTOM,
  TIMELINE_MARGIN_TOP,
} from '../epg.style';

export const defaultContext = makeDefaultStylingContext();

// NOTE: the amount of channels the placeholder grid is built of, clamped by the amount of rows an EPG displays at once
export const CHANNELS_COUNT = 5;

/**
 * NOTE: durations of the placeholder programs, in minutes. The sequence is rotated by a row index,
 * so rows do not end up with the very same box boundaries, as it never happens with a real schedule
 */
export const PROGRAM_DURATIONS = [30, 45, 15, 25, 45, 20, 35, 15, 40, 30];

export const TIMELINE_LABEL_WIDTH = {
  [HD]: 56,
  [FULL_HD]: 64,
};

// NOTE: the real timeline is marked every hour
export const TIMELINE_LABEL_INTERVAL = 60 * PIXELS_PER_MINUTE;

export const TITLE_WIDTH = {
  [HD]: 320,
  [FULL_HD]: 400,
};

export const DATE_SELECTOR_WIDTH = {
  [HD]: 100,
  [FULL_HD]: 150,
};

export const TO_LIVE_WIDTH = {
  [HD]: 120,
  [FULL_HD]: 148,
};

export const TAB_WIDTHS = {
  [HD]: [80, 100, 66],
  [FULL_HD]: [100, 130, 90],
};

export const TABS_GAP = {
  [HD]: 32,
  [FULL_HD]: 40,
};

export const getRowsHeight = screenSize =>
  Math.min(CHANNELS_COUNT, MAX_VISIBLE_ROWS[screenSize]) * (CHANNEL_SIZE[screenSize] + GAP) - GAP;

export const getTimelineOuterHeight = screenSize =>
  TIMELINE_HEIGHT[screenSize] + TIMELINE_MARGIN_TOP + TIMELINE_MARGIN_BOTTOM;

/**
 * Height of the whole placeholder, repeating the one HorizontalEpg.getOffsetsByBlockData reports for a real block
 */
export const getContentHeight = screenSize =>
  getRowsHeight(screenSize) + getTimelineOuterHeight(screenSize) + NAV_HEIGHT[screenSize];

const getGridWidth = (screenSize, contentPadding = 0) =>
  SCREEN_WIDTH[screenSize] - contentPadding - CHANNEL_SIZE[screenSize] - GAP;

/**
 * Builds a single channel row out of programs of a reasonable duration, filling the widest possible grid.
 * Boxes exceeding the visible width are cut by the Grid clipping, exactly as the real ones are.
 */
const makeProgramRow = (screenSize, rowIndex) => {
  const rowHeight = CHANNEL_SIZE[screenSize];
  const rowWidth = getGridWidth(screenSize);
  const children = [];
  let offset = 0;
  let index = 0;

  while (offset < rowWidth) {
    const duration = PROGRAM_DURATIONS[(rowIndex + index) % PROGRAM_DURATIONS.length];
    const itemWidth = duration * PIXELS_PER_MINUTE;

    children.push({
      type: SkeletonRectangle,
      // NOTE: the trailing gap belongs to the box itself, the way EpgBox keeps the grid in sync with the timeline
      w: Math.max(itemWidth - EPG_BOX_GAP, 1),
      h: rowHeight,
      x: offset,
      radius: EPG_BOX_BORDER_RADIUS[screenSize],
    });

    offset += itemWidth;
    index += 1;
  }

  return {
    y: rowIndex * (rowHeight + GAP),
    h: rowHeight,
    children,
  };
};

const makeTimelineMarks = screenSize => {
  const labelFont = getTimelineTextStyle(screenSize);
  const gridWidth = getGridWidth(screenSize);
  const children = [];

  for (let x = 0; x < gridWidth; x += TIMELINE_LABEL_INTERVAL) {
    children.push({
      type: SkeletonRectangle,
      x,
      y: Math.round((TIMELINE_HEIGHT[screenSize] - labelFont.lineHeight) / 2),
      w: TIMELINE_LABEL_WIDTH[screenSize],
      h: labelFont.lineHeight,
    });
  }

  return children;
};

export const getInitialStyle = _memoize(({ screenSize = FULL_HD, colorMode } = defaultContext) => {
  const channelSize = CHANNEL_SIZE[screenSize];
  const rowsHeight = getRowsHeight(screenSize);
  const arrowsWidth = ARROW_SIZE[screenSize] * 2 + GAP_RIGHT;
  const buttonY = Math.round((NAV_HEIGHT[screenSize] - BUTTON_HEIGHT[screenSize]) / 2);
  const tabsFont = getTabsFont(screenSize);
  const titleStyle = getTitleStyle({ screenSize, colorMode });

  return {
    Nav: {
      h: NAV_HEIGHT[screenSize],
      DateSelector: {
        type: SkeletonRectangle,
        y: buttonY,
        w: DATE_SELECTOR_WIDTH[screenSize],
        h: BUTTON_HEIGHT[screenSize],
        radius: BUTTON_BORDER_RADIUS[screenSize],
      },
      Tabs: {
        x: DATE_SELECTOR_WIDTH[screenSize] + GAP_LEFT,
        y: Math.round((NAV_HEIGHT[screenSize] - tabsFont.lineHeight) / 2),
        flex: {
          direction: 'row',
        },
        children: TAB_WIDTHS[screenSize].map((tabWidth, index, tabs) => ({
          type: SkeletonRectangle,
          w: tabWidth,
          h: tabsFont.lineHeight,
          flexItem: {
            marginRight: index === tabs.length - 1 ? 0 : TABS_GAP[screenSize],
          },
        })),
      },
      ToLive: {
        type: SkeletonRectangle,
        mountX: 1,
        x: v => v - arrowsWidth - TO_LIVE_MARGIN_RIGHT[screenSize] - GAP_RIGHT,
        y: buttonY,
        w: TO_LIVE_WIDTH[screenSize],
        h: BUTTON_HEIGHT[screenSize],
        radius: BUTTON_BORDER_RADIUS[screenSize],
      },
      Arrows: {
        w: arrowsWidth,
        mountX: 1,
        x: identity,
        y: Math.round((NAV_HEIGHT[screenSize] - ARROW_SIZE[screenSize]) / 2),
        ArrowLeft: {
          type: SkeletonRectangle,
          w: ARROW_SIZE[screenSize],
          h: ARROW_SIZE[screenSize],
          radius: ARROW_BORDER_RADIUS[screenSize],
        },
        ArrowRight: {
          type: SkeletonRectangle,
          x: ARROW_SIZE[screenSize] + GAP_RIGHT,
          w: ARROW_SIZE[screenSize],
          h: ARROW_SIZE[screenSize],
          radius: ARROW_BORDER_RADIUS[screenSize],
        },
      },
    },
    ChannelsVerticalClippingRoot: {
      clipping: true,
      w: channelSize,
      h: rowsHeight,
      Channels: {
        children: [...Array(CHANNELS_COUNT)].map((_, index) => ({
          type: SkeletonRectangle,
          w: channelSize,
          h: channelSize,
          y: index * (channelSize + GAP),
          radius: CHANNEL_BORDER_RADIUS[screenSize],
        })),
      },
    },
    Grid: {
      clipping: true,
      h: rowsHeight + getTimelineOuterHeight(screenSize),
      Timeline: {
        y: TIMELINE_MARGIN_TOP,
        h: TIMELINE_HEIGHT[screenSize],
        children: makeTimelineMarks(screenSize),
      },
      GridRoot: {
        y: getTimelineOuterHeight(screenSize),
        h: rowsHeight,
        children: [...Array(CHANNELS_COUNT)].map((_, rowIndex) => makeProgramRow(screenSize, rowIndex)),
      },
    },
    Title: {
      type: SkeletonRectangle,
      y: titleStyle.y,
      w: TITLE_WIDTH[screenSize],
      h: titleStyle.text.lineHeight,
    },
  };
}, cacheResolver);

/**
 * Positioning that depends on the data known after the placeholder is created: a block index and a content padding.
 * It repeats the layout HorizontalEpg applies on a data and a content padding change.
 */
export const getLayoutStyle = ({ screenSize = FULL_HD, blockIndex = 0, contentPadding = 0 } = defaultContext) => {
  const { items, title } = getBlockStyle({ hasTitle: true, isFirstBlock: blockIndex === 0, screenSize });
  const timelineOuterHeight = getTimelineOuterHeight(screenSize);
  const gridX = contentPadding + CHANNEL_SIZE[screenSize] + GAP;
  const gridWidth = getGridWidth(screenSize, contentPadding);

  return {
    Nav: {
      x: contentPadding,
      y: items.y,
      w: SCREEN_WIDTH[screenSize] - contentPadding - RIGHT_PADDING[screenSize],
    },
    ChannelsVerticalClippingRoot: {
      x: contentPadding,
      y: items.y + NAV_HEIGHT[screenSize] + timelineOuterHeight,
    },
    Grid: {
      x: gridX,
      y: items.y + NAV_HEIGHT[screenSize],
      w: gridWidth,
      GridRoot: {
        w: gridWidth,
      },
    },
    Title: {
      x: contentPadding,
      ...title,
    },
  };
};
